import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { listThreads, getThread, createThread, addPost } from "../lib/api";
import { ArrowRight, MessageSquare, Loader2, Send } from "lucide-react";

export const ForumList = () => {
  const [threads, setThreads] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("allgemein");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { listThreads().then(setThreads); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/auth/login?redirect=/forum"); return; }
    setBusy(true);
    try {
      const t = await createThread({ title, body, category });
      nav(`/forum/${t.id}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-28 min-h-screen" data-testid="forum-list">
      <div className="container-sat max-w-[1100px] py-10">
        <div className="overline mb-3">SAT · Community</div>
        <h1 className="headline-xl text-4xl md:text-6xl">Forum</h1>
        <p className="serif italic text-xl text-white/55 mt-4 max-w-2xl">Ein Raum für ehrliche Worte. Sprich, höre, wachse.</p>

        <div className="mt-8 flex justify-between items-center">
          <div className="text-sm text-white/55">{threads.length} Threads</div>
          <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm" data-testid="forum-new-thread">{showNew ? "Abbrechen" : "Neuer Thread"}</button>
        </div>

        {showNew && (
          <form onSubmit={submit} className="glass rounded-2xl p-6 mt-6 space-y-3" data-testid="forum-new-form">
            <input required className="sat-input" placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)} />
            <select className="sat-input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {["allgemein", "beziehung", "selbstwert", "berufung", "familie", "mentor"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <textarea required className="sat-input" placeholder="Deine Worte…" value={body} onChange={(e) => setBody(e.target.value)} rows={5}></textarea>
            <button disabled={busy} className="btn-primary">{busy ? <Loader2 className="animate-spin" size={16} /> : <>Veröffentlichen <Send size={14} /></>}</button>
          </form>
        )}

        <div className="mt-8 space-y-3" data-testid="forum-threads">
          {threads.length === 0 && <div className="text-center py-12 text-white/45"><MessageSquare className="mx-auto mb-3" /> Noch keine Threads — sei die/der Erste.</div>}
          {threads.map((t) => (
            <Link key={t.id} to={`/forum/${t.id}`} className="block glass rounded-xl p-5 hover:bg-white/[0.04] transition" data-testid={`forum-thread-${t.id}`}>
              <div className="flex items-center gap-3 mb-1">
                <span className="overline">{t.category}</span>
                <span className="text-xs text-white/40">·</span>
                <span className="text-xs text-white/45">{t.author_name}</span>
              </div>
              <div className="serif text-xl">{t.title}</div>
              <div className="text-white/55 text-sm mt-2 line-clamp-2">{t.body}</div>
              <div className="text-xs text-white/40 mt-3 flex items-center gap-1"><MessageSquare size={11} /> {t.post_count} Antworten</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ForumThread = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => getThread(id).then(setData);
  useEffect(() => { reload(); }, [id]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!user) { nav(`/auth/login?redirect=/forum/${id}`); return; }
    setBusy(true);
    try {
      await addPost(id, reply);
      setReply("");
      await reload();
    } finally { setBusy(false); }
  };

  if (!data) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-28 min-h-screen" data-testid={`forum-thread-page-${id}`}>
      <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-10">
        <Link to="/forum" className="btn-ghost text-sm">← Forum</Link>
        <div className="mt-6">
          <span className="overline">{data.thread.category}</span>
          <h1 className="headline-xl text-4xl md:text-5xl mt-3">{data.thread.title}</h1>
          <div className="text-sm text-white/50 mt-2">{data.thread.author_name} · {data.thread.created_at?.slice(0, 10)}</div>
          <p className="text-white/80 text-lg leading-relaxed mt-6 whitespace-pre-line">{data.thread.body}</p>
        </div>

        <div className="mt-12 space-y-4">
          <div className="overline mb-2">{data.posts.length} Antworten</div>
          {data.posts.map((p) => (
            <div key={p.id} className="glass rounded-xl p-5" data-testid={`forum-post-${p.id}`}>
              <div className="text-sm text-white/50 mb-2">{p.author_name} · {p.created_at?.slice(0, 10)}</div>
              <p className="text-white/85 whitespace-pre-line">{p.body}</p>
            </div>
          ))}
        </div>

        <form onSubmit={sendReply} className="glass rounded-2xl p-6 mt-8 space-y-3" data-testid="forum-reply-form">
          <textarea required className="sat-input" placeholder="Deine Antwort…" rows={4} value={reply} onChange={(e) => setReply(e.target.value)}></textarea>
          <button disabled={busy} className="btn-primary text-sm">{busy ? <Loader2 className="animate-spin" size={14} /> : <>Antworten <Send size={14} /></>}</button>
        </form>
      </div>
    </div>
  );
};

export default ForumList;
